/**
 * Layout for every `/student/*` route. Mirrors `<TeacherShell>` so the
 * branding, top bar, banner, and side nav stay consistent across personas
 * — the only difference is the nav item set and the displayed name (a
 * pseudonym, never a real student name).
 */

import { NavLink, Outlet } from 'react-router-dom';
import { Banner } from './Banner';
import { useSession } from '../auth/SessionProvider';

const NAV_ITEMS: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/student/dashboard', label: 'Dashboard' },
  { to: '/student/courses', label: 'Courses' },
  { to: '/student/assistant', label: 'Study Assistant' },
  { to: '/student/tutor', label: 'Homework Tutor' },
  { to: '/student/progress', label: 'Progress' },
];

export function StudentShell() {
  const { session, signOut } = useSession();
  // The UI never renders a real name. The session userId for a student
  // session is itself a mock pseudonym (`mock-user-student`) — we display
  // it verbatim as the principal's pseudonymous identifier.
  const pseudonym = session?.principal.userId ?? 'Anonymous student';

  return (
    <div className="shell">
      <header className="shell__topbar" role="banner">
        <div className="shell__brand">
          <strong>Teacher AI Hub</strong>
          <span className="shell__brand-tag">Student MVP</span>
        </div>
        <div className="shell__user">
          <span aria-label="Signed-in student pseudonym">
            <code>{pseudonym}</code>
          </span>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={signOut}
            data-testid="sign-out"
          >
            Sign out
          </button>
        </div>
      </header>
      <Banner />
      <div className="shell__body">
        <nav className="shell__nav" aria-label="Student navigation">
          <ul>
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    isActive ? 'shell__nav-link is-active' : 'shell__nav-link'
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="shell__main" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
